import clsx from 'clsx';
import { PrimitiveAtom, useAtom } from 'jotai';
import React from 'react';
import { AnyIcon } from '../../dashboard/Dashboard.types';

// #region TabsContainerInterface
export interface TabsContainer {
    stateAtom: PrimitiveAtom<number>;
    tabOnly?: boolean;
}

export type TabsContainerType = TabsContainer & React.ComponentPropsWithoutRef<'div'>;

// #region TabsContainer
export const TabsContainer: React.FunctionComponent<TabsContainerType> = (props: TabsContainerType): JSX.Element => {
    const { className, stateAtom, children, tabOnly = false, ...remaining } = props;

    const [activeTab, setActiveTab] = useAtom(stateAtom);

    return (
        <div
            className={clsx(className, 'flex flex-col transition-all')}
            {...remaining}
        >
            {/* Tab bar */}
            <div className="border-b border-zinc-200 dark:border-zinc-700">
                <nav className="flex gap-1 -mb-px" role="tablist">
                    {
                        React.Children.map(children, (child: React.ReactNode, index: number) => {
                            if (!React.isValidElement(child)) return null;

                            const isActive = activeTab === index;
                            const Icon: AnyIcon | undefined = child.props.tabIcon;
                            const label: string = child.props.tabName;
                            const count: number | undefined = child.props.count;

                            return (
                                <button
                                    key={child.key}
                                    role="tab"
                                    aria-selected={isActive}
                                    onClick={() => setActiveTab(index)}
                                    className={clsx(
                                        'group relative flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors outline-none',
                                        'rounded-t-lg',
                                        isActive
                                            ? 'text-sky-600 dark:text-sky-400'
                                            : 'text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-300',
                                    )}
                                >
                                    {Icon && (
                                        <Icon
                                            className={clsx(
                                                'h-4 w-4 transition-colors shrink-0',
                                                isActive
                                                    ? 'text-sky-600 dark:text-sky-400'
                                                    : 'text-zinc-400 dark:text-zinc-500 group-hover:text-zinc-500 dark:group-hover:text-zinc-400',
                                            )}
                                        />
                                    )}
                                    <span className="hidden sm:inline">{label}</span>
                                    {count !== undefined && (
                                        <span
                                            className={clsx(
                                                'hidden sm:inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-xs font-medium transition-colors',
                                                isActive
                                                    ? 'bg-sky-100 text-sky-700 dark:bg-sky-900/50 dark:text-sky-300'
                                                    : 'bg-surface-muted text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400 group-hover:bg-surface-strong dark:group-hover:bg-zinc-700',
                                            )}
                                        >
                                            {count}
                                        </span>
                                    )}
                                    {/* Active indicator line */}
                                    <span
                                        className={clsx(
                                            'absolute bottom-0 left-0 right-0 h-0.5 rounded-full transition-all',
                                            isActive
                                                ? 'bg-sky-600 dark:bg-sky-400'
                                                : 'bg-transparent group-hover:bg-zinc-300 dark:group-hover:bg-zinc-600',
                                        )}
                                    />
                                </button>
                            );
                        })
                    }
                </nav>
            </div>

            {/* Tab content */}
            {
                tabOnly === false ? (
                    <div className="flex h-full">
                        {
                            React.Children.map(children, (child: React.ReactNode, index: number) => {
                                if (activeTab !== index) return null;

                                if (React.isValidElement(child)) {
                                    return React.cloneElement(child, child.props);
                                }
                            })
                        }
                    </div>
                ) : null
            }
        </div>
    );
};

// #region TabInterface
export interface Tab {
    key: string;
    tabName: string;
    tabIcon?: AnyIcon;
    count?: number;
    bodyClassName?: string;
}

export type TabType = Tab & React.ComponentPropsWithoutRef<'div'>;

// #region Tab
export const Tab: React.FunctionComponent<TabType> = (props: TabType): JSX.Element => {

    const { children, bodyClassName } = props;

    return (
        <div className={clsx(bodyClassName ?? 'p-4', 'w-full')}>
            {children}
        </div>
    );
};
